// Drag-and-drop image uploader for the admin panel.
// Ported 1:1 from miluliem (apps/admin/src/components/admin/ImageUpload.tsx)
// so behaviour matches across projects — keep them in sync when changing.

import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Image as ImageIcon, Upload, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { uploadImageToR2, type UploadBucket } from "@/lib/r2-upload";
import { mediaSrc } from "@/lib/media";

interface ImageUploadProps {
  value?: string;
  onChange: (url: string) => void;
  bucket: UploadBucket;
  /** When true, skip the square-crop/whitespace-trim processing and upload
   *  as-is. Use for banners + collection covers (preserve aspect ratio). */
  preserveAspectRatio?: boolean;
  className?: string;
  placeholder?: string;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  bucket,
  preserveAspectRatio = false,
  className,
  placeholder = "Click or drag to upload image",
}) => {
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;
      try {
        setUploading(true);
        const url = await uploadImageToR2(file, { bucket, preserveAspectRatio });
        onChange(url);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : typeof err === "string" ? err : "Unknown error";
        console.error("[ImageUpload]", message);
        alert(`Image upload failed: ${message}`);
      } finally {
        setUploading(false);
      }
    },
    [bucket, preserveAspectRatio, onChange],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    maxFiles: 1,
    multiple: false,
  });

  const removeImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
  };

  return (
    <div className={cn("space-y-2", className)}>
      {value ? (
        <div
          className={cn(
            "relative group w-full border border-border rounded-lg overflow-hidden bg-muted/30",
            preserveAspectRatio ? "aspect-video max-w-[420px]" : "aspect-square max-w-[200px]",
          )}
        >
          <img
            // Legacy/imported images are stored as bare R2 paths (no host);
            // mediaSrc() prefixes VITE_LEGACY_MEDIA_URL so they render. Freshly
            // uploaded absolute URLs pass through unchanged.
            src={mediaSrc(value) ?? value}
            alt="Preview"
            className="w-full h-full object-contain transition-transform group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Button
              variant="destructive"
              size="icon"
              className="h-8 w-8 rounded-full"
              onClick={removeImage}
            >
              <X size={16} />
            </Button>
          </div>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={cn(
            "border-2 border-dashed rounded-lg p-6 transition-all cursor-pointer flex flex-col items-center justify-center text-center gap-2 min-h-[160px]",
            isDragActive
              ? "border-primary bg-primary/5 scale-[0.98]"
              : "border-border hover:border-primary/50 hover:bg-muted/30",
            uploading && "opacity-50 cursor-not-allowed pointer-events-none",
          )}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <>
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
              <p className="text-sm text-foreground font-medium animate-pulse">Uploading…</p>
            </>
          ) : (
            <>
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mb-1">
                {isDragActive ? (
                  <Upload className="h-5 w-5 text-primary" />
                ) : (
                  <ImageIcon className="h-5 w-5 text-primary" />
                )}
              </div>
              <div>
                <p className="text-sm text-foreground font-medium">
                  {isDragActive ? "Drop to upload" : placeholder}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  JPG, PNG, WEBP (max 10MB)
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
